// src/components/LinkedJobsButton.js
// ---------------------------------------------------------------------------
// "Jobs Linked (N)" button for a job's details. Click it for a small popup of
// every job in the same chain, in order from the first to the last:
//
//   - the work order family: 3101, 3101-2, 3101-3 ... (and PJMC-10025-2 ...)
//     -> watched LIVE, so a job added by Continue / stem WO shows up at once
//   - jobs linked by Continue even if their WO doesn't match
//     (continuedFromJobId, projectContinuedFromJobId, previousJobId), up and down
//
// Read-only. Click a row to open that job's details popup (when the page
// passes onOpenJob) - edits there save to THAT job only.
//
//   <LinkedJobsButton job={selectedJob} onOpenJob={(j) => setSelectedJob(j)} />
//   Calendars: onOpenJob={(j) => open(pickJobToOpen(j, jobsByDate, jobs))}
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getWOStem } from "../utils/continueJobUtils";
import "./LinkedJobsButton.css";
import { useLanguage, TranslatedText } from "../i18n";

const LINK_FIELDS = ["continuedFromJobId", "projectContinuedFromJobId", "previousJobId"];

/** "3101-3" -> "3101" (all the way down). */
export const rootWOStem = (wo) => {
  let cur = String(wo ?? "").trim();
  for (let i = 0; i < 8; i += 1) {
    const next = getWOStem(cur);
    if (next === cur) break;
    cur = next;
  }
  return cur;
};

const dayKey = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  if (v?.toDate) return v.toDate().toISOString().slice(0, 10);
  if (v?.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return "";
};
const mdy = (k) => (k ? `${k.slice(5, 7)}/${k.slice(8, 10)}/${k.slice(0, 4)}` : "");
const suffixNum = (wo) => {
  const m = String(wo ?? "").match(/-(\d+)$/);
  return m && /\d/.test(String(wo).slice(0, -m[0].length)) ? Number(m[1]) : 1;
};
const techs = (j) => (Array.isArray(j.techAssign) ? j.techAssign.filter(Boolean).join(", ") : j.techAssign || "");

/** First to last: by start date, then WO number (3101, -2, -3), undated last. */
export const orderLinkedJobs = (list) =>
  list.slice().sort((a, b) => {
    const da = dayKey(a.startDate) || "9999-99-99";
    const dbb = dayKey(b.startDate) || "9999-99-99";
    if (da !== dbb) return da < dbb ? -1 : 1;
    return suffixNum(a.wo) - suffixNum(b.wo);
  });

/**
 * Which copy of a linked job a calendar should open in its details popup.
 * Prefers the calendar's own rendered copy (it carries the occurrence date the
 * notes / photos / complete / invoice logic keys on). A recurring job has one
 * rendered copy per visit, so pick the visit on the job's start day, else the
 * next upcoming visit, else the latest one shown. Falls back to the page's
 * plain job list, then to the linked row itself (e.g. an Invoice Sent job the
 * calendar doesn't load).
 */
export const pickJobToOpen = (linked, jobsByDate, plainJobs) => {
  if (!linked?.id) return linked;
  const rendered = Object.values(jobsByDate || {})
    .flat()
    .filter((j) => j && j.id === linked.id)
    .sort((a, b) => String(a.__renderDate || "").localeCompare(String(b.__renderDate || "")));
  if (rendered.length === 1) return rendered[0];
  if (rendered.length > 1) {
    const start = dayKey(linked.startDate);
    const today = new Date().toISOString().slice(0, 10);
    return (
      rendered.find((j) => j.__renderDate === start) ||
      rendered.find((j) => String(j.__renderDate || "") >= today) ||
      rendered[rendered.length - 1]
    );
  }
  return (Array.isArray(plainJobs) && plainJobs.find((j) => j.id === linked.id)) || linked;
};

const LinkedJobsButton = ({ job, onOpenJob, label = "Jobs Linked" }) => {
  const { t } = useLanguage();
  const [family, setFamily] = useState({}); // id -> job (live WO family)
  const [links, setLinks] = useState({}); // id -> job (Continue links, refreshed on open)
  const [open, setOpen] = useState(false);

  const stem = useMemo(() => rootWOStem(job?.wo), [job?.wo]);

  // LIVE: every job whose WO is in this family.
  useEffect(() => {
    if (!job?.id || !stem) return undefined;
    const stemKey = stem.toUpperCase();
    const lower = stem.toLowerCase();
    const variants = Array.from(new Set([stem, stem.toUpperCase(), lower, lower.charAt(0).toUpperCase() + lower.slice(1)]));
    const buckets = {};
    const publish = () => {
      const merged = {};
      Object.values(buckets).forEach((m) => Object.assign(merged, m));
      setFamily(merged);
    };
    const keep = (d) => rootWOStem(d.data()?.wo).toUpperCase() === stemKey;
    const queries = variants.map((v) => query(collection(db, "jobs"), where("wo", ">=", v), where("wo", "<=", `${v}`)));
    if (/^\d+$/.test(stem)) queries.push(query(collection(db, "jobs"), where("wo", "==", Number(stem))));
    const unsubs = queries.map((q, i) =>
      onSnapshot(
        q,
        (snap) => {
          const m = {};
          snap.docs.forEach((d) => {
            if (keep(d)) m[d.id] = { id: d.id, ...d.data() };
          });
          buckets[i] = m;
          publish();
        },
        (err) => console.warn("Jobs Linked: live list stopped", err)
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [job?.id, stem]);

  // Continue links (parents up, children down) - fetched as soon as the job is
  // shown (so the button's count is right before you click it) and again each
  // time the popup opens. Cleared first so a previously opened job's links
  // never bleed into this one.
  useEffect(() => {
    setLinks({});
    if (!job?.id) return undefined;
    let live = true;
    (async () => {
      const found = {};
      const seen = new Set([job.id]);
      // up
      let cur = job;
      for (let i = 0; i < 40; i += 1) {
        const parentId = LINK_FIELDS.map((f) => cur?.[f]).find(Boolean);
        if (!parentId || seen.has(parentId)) break;
        seen.add(parentId);
        const snap = await getDoc(doc(db, "jobs", parentId)).catch(() => null);
        if (!snap || !snap.exists()) break;
        cur = { id: snap.id, ...snap.data() };
        found[cur.id] = cur;
      }
      // down from the top of the chain
      const queue = [cur?.id || job.id, job.id];
      while (queue.length && Object.keys(found).length < 120) {
        const id = queue.shift();
        const snaps = await Promise.all(
          LINK_FIELDS.map((f) => getDocs(query(collection(db, "jobs"), where(f, "==", id))).catch(() => ({ docs: [] })))
        );
        snaps.forEach((s) =>
          s.docs.forEach((d) => {
            if (found[d.id] || d.id === job.id) return;
            found[d.id] = { id: d.id, ...d.data() };
            queue.push(d.id);
          })
        );
      }
      if (live) setLinks(found);
    })();
    return () => {
      live = false;
    };
    // Re-run only when a different job is shown, its Continue links change, or
    // the popup opens - not on every unrelated edit to the job object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job?.id, job?.continuedFromJobId, job?.projectContinuedFromJobId, job?.previousJobId]);

  const ordered = useMemo(() => {
    const all = { ...links, ...family };
    if (job?.id) all[job.id] = { ...(all[job.id] || {}), ...job, id: job.id };
    return orderLinkedJobs(Object.values(all).filter((j) => j.status !== "Deleted"));
  }, [family, links, job]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!job?.id) return null;
  const count = ordered.length;

  const stop = (e) => e.stopPropagation();

  return (
    <>
      <button
        type="button"
        className="ljb-btn"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={t("See every job in this chain, first to last")}
      >
        🔗 {t(label)} ({count})
      </button>

      {open &&
        createPortal(
          <div
            className="ljb-overlay"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            onMouseDown={stop}
          >
            <div className="ljb-pop" role="dialog" aria-label="Linked jobs" onClick={stop}>
              <div className="ljb-head">
                <div>
                  <strong>{t("Linked jobs")}</strong>
                  <span className="ljb-muted">
                    {" "}
                    · WO {stem || "—"} · {count === 1 ? t("1 job") : t("{count} jobs", { count })} · {t("updates live")}
                  </span>
                </div>
                <button type="button" className="ljb-close" onClick={() => setOpen(false)}>
                  ✕
                </button>
              </div>

              {count <= 1 ? (
                <div className="ljb-muted ljb-empty">
                  {t("No other linked jobs yet. When this job is continued (or a stem WO like {wo}-2 is added), it shows up here.", {
                    wo: stem || "3101",
                  })}
                </div>
              ) : (
                <ol className="ljb-list">
                  {ordered.map((j, i) => {
                    const start = dayKey(j.startDate);
                    const end = dayKey(j.endDate);
                    const current = j.id === job.id;
                    const clickable = !current && typeof onOpenJob === "function";
                    return (
                      <li
                        key={j.id}
                        className={`ljb-row${current ? " ljb-row--current" : ""}${clickable ? " ljb-row--click" : ""}`}
                        onClick={() => {
                          if (!clickable) return;
                          setOpen(false);
                          onOpenJob(j);
                        }}
                        title={clickable ? t("Open this job") : ""}
                      >
                        <span className="ljb-num">{i + 1}</span>
                        <div className="ljb-main">
                          <div className="ljb-line1">
                            <b>WO {j.wo || "—"}</b>
                            {current && <span className="ljb-tag">{t("This job")}</span>}
                            {j.status && <span className={`ljb-status ljb-status--${String(j.status).toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t(j.status)}</span>}
                          </div>
                          <div className="ljb-line2">
                            {start ? `${mdy(start)}${end && end !== start ? ` – ${mdy(end)}` : ""}` : t("Not scheduled")}
                            {techs(j) ? ` · ${techs(j)}` : ""}
                          </div>
                          {(j.scope || j.siteName) && (
                            <div className="ljb-line3">
                              {j.siteName || ""}
                              {j.siteName && j.scope ? " · " : ""}
                              {j.scope ? <TranslatedText text={String(j.scope).slice(0, 120)} showToggle={false} /> : null}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default LinkedJobsButton;
